import React from 'react';
import {
  ApplicationIcon,
  Box,
  Button,
  Loader,
  TabContent,
  Typography,
} from '@saviynt/design-system';
import { format, isValid } from 'date-fns';

import {
  normalizeRiskPriority,
  SodFieldSet,
  SodRiskPriorityChip,
  SodSeverityChip,
  SodTextFieldSet,
} from '../../../../../shared/components';
import {
  CF_SEC_BLD,
  EMPTY_FIELD_PLACEHOLDER,
  ENTITLEMENT_DETAILS_TABS,
} from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import { useEntitlementDetails } from '../../../hooks';

import './EntitlementSummary.css';

/**
 * EntitlementSummary Component
 *
 * Displays comprehensive summary information for a selected entitlement in the details view.
 * Shows entitlement metadata, application details, severity levels, and descriptive information
 * in a structured two-column layout with fallback values for missing data.
 *
 * @component
 * @returns {React.ReactElement} TabContent with entitlement summary information including:
 *   - Lifecycle dates (Start/End)
 *   - Application info with logo
 *   - Entitlement type and value
 *   - Description and glossary
 *   - Severity chips (Sox, Sys, Privilege)
 *
 * @example
 * <EntitlementSummary />
 *
 * @dependencies
 * - Redux: selectedEntitlement from state.aag.details.entitlementSummary
 * - Hooks: useInvertedLocalize for localization
 * - Components: SodFieldSet, SodSeverityChip, ApplicationIcon
 */
function EntitlementSummary() {
  const l = useInvertedLocalize();
  const { entitlementDetails, isLoading, isError, refetch } =
    useEntitlementDetails();

  const {
    global: { globalDateFormat },
    details: { activeTab },
  } = useTypedSelector((state) => state.aag);

  if (isLoading) {
    return (
      <TabContent
        className='Sod-EntitlementDetails-tabContent Sod-EntitlementDetails-loaderWrapper'
        value={activeTab ?? ENTITLEMENT_DETAILS_TABS.TAB_SUMMARY}
        tabValue={ENTITLEMENT_DETAILS_TABS.TAB_SUMMARY}>
        <Box className='Sod-Violations-loaderWrapper'>
          <Loader kind='dots' format='inline' color='brand' />
        </Box>
      </TabContent>
    );
  }

  if (isError) {
    return (
      <TabContent
        className='Sod-EntitlementDetails-tabContent Sod-EntitlementDetails-loaderWrapper'
        value={activeTab ?? ENTITLEMENT_DETAILS_TABS.TAB_SUMMARY}
        tabValue={ENTITLEMENT_DETAILS_TABS.TAB_SUMMARY}>
        <Box className='Sod-SidePanelSummary-loaderWrapper'>
          <Typography kind='body2'>
            {l('Error loading summary details, please try again.')}
          </Typography>
          <Button
            type='button'
            kind='outlined'
            isEnabled
            onClick={() => {
              refetch().catch(() => {
                // Error handling is managed by the hook
              });
            }}>
            {l('Retry')}
          </Button>
        </Box>
      </TabContent>
    );
  }

  const normalizedRiskPriority = normalizeRiskPriority(
    entitlementDetails?.riskObjects[0]?.riskPriority
  );
  const riskFieldValue = normalizedRiskPriority ? (
    <SodRiskPriorityChip
      priority={normalizedRiskPriority}
      riskScore={null}
      isPrefixInterpoint={false}
      isOnlyLabel
    />
  ) : (
    <Typography kind='body2' color={CF_SEC_BLD}>
      {EMPTY_FIELD_PLACEHOLDER}
    </Typography>
  );

  return (
    <TabContent
      className='Sod-EntitlementDetails-tabContent'
      value={activeTab ?? ENTITLEMENT_DETAILS_TABS.TAB_SUMMARY}
      tabValue={ENTITLEMENT_DETAILS_TABS.TAB_SUMMARY}>
      <Box className='Sod-EntitlementSummary-tabContentWrapper'>
        <Box tag='div' className='Sod-EntitlementSummary-content'>
          <Box className='Sod-EntitlementSummary-columns'>
            <SodTextFieldSet
              label={l('Start')}
              value={
                entitlementDetails?.createdDate &&
                isValid(entitlementDetails.createdDate)
                  ? format(
                      new Date(entitlementDetails.createdDate),
                      globalDateFormat
                    )
                  : entitlementDetails?.createdDate
              }
            />
            <SodTextFieldSet
              label={l('End')}
              value={
                entitlementDetails?.lastUpdated &&
                isValid(entitlementDetails.lastUpdated)
                  ? format(
                      new Date(entitlementDetails.lastUpdated),
                      globalDateFormat
                    )
                  : entitlementDetails?.lastUpdated
              }
            />
          </Box>

          <Box tag='span' className='Sod-EntitlementSummary-border' />

          <Box className='Sod-EntitlementSummary-columns'>
            <SodFieldSet
              label={l('Application')}
              value={
                <Box tag='span' className='Sod-EntitlementSummary-application'>
                  <ApplicationIcon
                    logo={entitlementDetails?.endPointLogo ?? l('Not Provided')}
                    size='xxSmall'
                  />
                  <Typography kind='body2'>
                    {entitlementDetails?.endpointName ?? l('Not Provided')}
                  </Typography>
                </Box>
              }
            />
            <SodTextFieldSet
              label={l('Entitlement Type')}
              value={entitlementDetails?.entitlementTypeName}
            />
          </Box>

          <SodTextFieldSet
            label={l('Description')}
            value={entitlementDetails?.entitlementDescription}
          />
          <SodTextFieldSet
            label={l('Glossary')}
            lineLimit={2}
            value={entitlementDetails?.glossary}
          />

          <Box className='Sod-EntitlementSummary-columns'>
            <SodFieldSet
              label={l('Sox Critical')}
              value={
                <SodSeverityChip
                  text={entitlementDetails?.entitlementSoxSeverity ?? 'None'}
                />
              }
            />
            <SodFieldSet
              label={l('Sys Critical')}
              value={
                <SodSeverityChip
                  text={entitlementDetails?.entitlementSysSeverity ?? 'None'}
                />
              }
            />

            <SodFieldSet label={l('Risk')} value={riskFieldValue} />

            <SodFieldSet
              label={l('Privileged')}
              value={
                <SodSeverityChip
                  text={
                    entitlementDetails?.entitlementPrivilegeSeverity ?? 'None'
                  }
                />
              }
            />
          </Box>
        </Box>
      </Box>
    </TabContent>
  );
}

export default EntitlementSummary;
